import {
  useQueryClient,
  type QueryKey,
  type useSuspenseQueries as useSuspenseQueriesFromTanstackReactQuery,
  type useSuspenseQuery as useSuspenseQueryFromTanstackReactQuery,
} from "@tanstack/react-query";
import { useRef } from "react";
import { createPlainConsola } from "@-/dev.improve.haniverse.lib.logger";
import {
  describeDeterministicQueryError,
  describeTransientNetworkError,
  isDeterministicQueryError,
  isTransientNetworkError,
} from "@-/dev.improve.haniverse.lib.error-classification";
import { captureSentryException } from "@-/dev.improve.haniverse.lib.sentry";
import { instrumentSuspenseWait } from "@-/dev.improve.haniverse.lib.sentry/perf";
import { useQueries, useQuery } from "./use-navigation-focused-queries.ts";

const consola = createPlainConsola({ defaults: { tag: "usePrefetchedSuspenseQuery" } });
const SENTRY_TAG_VALUE_MAX_LENGTH = 180;

function stringPrefixKey(queryKey: readonly unknown[]): string {
  const prefix: string[] = [];
  for (const segment of queryKey) {
    if (typeof segment !== "string") {
      break;
    }
    prefix.push(segment);
  }
  return JSON.stringify(prefix);
}

function stringifyQueryKey(queryKey: QueryKey | readonly unknown[]): string {
  try {
    return JSON.stringify(queryKey, (_key, value: unknown) => {
      if (typeof value === "bigint") {
        return value.toString();
      }

      if (typeof value === "function") {
        return `[function ${value.name || "anonymous"}]`;
      }

      return value;
    });
  } catch (error) {
    return `[unserializable query key: ${error instanceof Error ? error.message : String(error)}]`;
  }
}

function truncateSentryTagValue(value: string): string {
  if (value.length <= SENTRY_TAG_VALUE_MAX_LENGTH) {
    return value;
  }

  return `${value.slice(0, SENTRY_TAG_VALUE_MAX_LENGTH - 3)}...`;
}

/**
 * Query-key hashes already reported as failed this session.
 *
 * A failure is re-evaluated on EVERY render of the failing component, and a failing query
 * re-renders continuously (see `suspenseRetryDelayMs`). Reporting per render turned a single
 * broken query into an unbounded stream of identical Sentry events and console lines — the
 * `d0.description` incident emitted one pair per grapheme per render. Report the FIRST occurrence
 * for a key and stay silent afterwards; the retained entry also keeps a remount from re-reporting.
 * Module scope (not a ref) so every component instance observing the same key shares the record.
 */
const reportedFailedKeys = new Set<string>();

function reportOnce(seen: Set<string>, key: string, report: () => void): void {
  if (seen.has(key)) {
    return;
  }
  seen.add(key);
  report();
}

/**
 * Exponential backoff for a suspense query that keeps failing, capped at {@link MAX_RETRY_DELAY_MS}.
 *
 * `usePrefetchedSuspenseQuery` re-issues its query through `ensureQueryData` whenever it has no
 * data, so with the root client's `retry: false` a deterministic failure (a client/DB schema skew,
 * say) becomes a hot loop: fail → re-render → refetch → fail. Backing each attempt off turns that
 * into a slow, bounded poll that still recovers on its own once the backend is healthy again.
 */
const BASE_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30_000;
// The budget for a failure nobody has classified: bounded so it reaches the error boundary in ~15s
// instead of polling forever behind a spinner, but non-zero because an unclassified failure may
// still be a worker cold start or a one-off. A failure that IS classified as deterministic (a
// schema skew, a query D1 refuses to compile — see `isDeterministicQueryError`) gets NO retries at
// all: the request is wrong, not unlucky, and every retry is dead time before the user is told.
// Past this, the retryable error UI is the honest answer.
const MAX_RETRY_ATTEMPTS = 3;
// A TRANSIENT transport failure gets a longer, quieter runway than a deterministic one. The budget
// above exists to stop a hot loop on a bug that no amount of waiting fixes; a canceled request or a
// network blip is the opposite case — nothing is wrong with the request, so retrying it is the
// correct answer rather than a stall. Kept bounded so a permanently-unreachable backend still
// reaches the (escapable) error UI instead of spinning forever.
const MAX_TRANSIENT_RETRY_ATTEMPTS = 6;
// A cancellation is a LIFECYCLE event (screen unmounted mid-flight, app backgrounded, bundle
// reloaded), not a signal that the server is struggling. Backing off from it is pure dead time, so
// it is re-issued almost immediately. This is what keeps the iOS `FetchRequestCanceledException`
// from ever reaching a user: six near-instant attempts instead of three spread over ~14s.
const CANCELED_RETRY_DELAY_MS = 150;

function suspenseShouldRetry(failureCount: number, error: unknown): boolean {
  // A deterministic failure is surfaced on the FIRST attempt. On staging a schema drift between
  // the app and the worker's D1 spent three retries (~15s of spinner) before the error screen, on
  // the one screen that surfaced it at all — and the retries could never have changed the answer.
  if (isDeterministicQueryError(error)) {
    return false;
  }
  return (
    failureCount <
    (isTransientNetworkError(error) ? MAX_TRANSIENT_RETRY_ATTEMPTS : MAX_RETRY_ATTEMPTS)
  );
}

function suspenseRetryDelayMs(failureCount: number, error?: unknown): number {
  if (describeTransientNetworkError(error) === "canceled") {
    return CANCELED_RETRY_DELAY_MS;
  }
  return Math.min(BASE_RETRY_DELAY_MS * 2 ** failureCount, MAX_RETRY_DELAY_MS);
}

function captureSuspenseQueryError(error: unknown, queryKey: QueryKey | readonly unknown[]): void {
  const serializedQueryKey = stringifyQueryKey(queryKey);

  captureSentryException(error, {
    extra: { queryKey, serializedQueryKey },
    tags: {
      boundary: "suspense-page",
      phase: "query-failed",
      "suspense.query_key": truncateSentryTagValue(serializedQueryKey),
      // The safety valve on the transient classification: a failure that EXHAUSTED the longer
      // transient budget still reports, tagged with its category. If the classifier ever starts
      // swallowing genuine defects, that shows up here as a spike in transient-tagged exhaustions
      // rather than as silence.
      "suspense.transient": describeTransientNetworkError(error) ?? "none",
      // The other half of the classification: a schema drift or an uncompilable query groups apart
      // from both network noise and genuine unknowns, so "the app is out of date for its data" is
      // one Sentry issue rather than a spike inside "Something went wrong".
      "suspense.deterministic": describeDeterministicQueryError(error) ?? "none",
    },
  });
}

/**
 * Prefetch-aware suspense: suspends AT MOST ONCE per query-key prefix. Backed by a plain
 * `useQuery` whose `placeholderData` is the last good value seen for this key's string prefix
 * ({@link stringPrefixKey}), it only throws (suspends) on the very first load for that prefix, when
 * there is no data at all. Every later refetch — a key rotation that keeps the same prefix, or an
 * invalidation of the same key — resolves in place against the retained value and never re-suspends,
 * so a suspending boundary above it isn't torn down on a background update. Drop-in for TanStack's
 * `useSuspenseQuery` (same signature).
 *
 * That first suspension is normal, not a defect: a navigation commits on the press, before its
 * destination's data exists, and the enclosing `SuspensePageBoundary` draws the page-shaped
 * fallback until it lands. Warming the query earlier (a navigator's `prefetch` on press-in) only
 * makes it rarer. A suspension that never ends is bounded by the boundary, not reported here.
 */
export const usePrefetchedSuspenseQuery: typeof useSuspenseQueryFromTanstackReactQuery = ((
  options,
) => {
  const queryClient = useQueryClient();
  const lastGoodByPrefixRef = useRef<Map<string, unknown>>(new Map());

  const prefix = stringPrefixKey(options.queryKey);

  const result = useQuery({
    ...options,
    placeholderData: (() => lastGoodByPrefixRef.current.get(prefix)) as never,
    // A suspense read that fails deterministically must not spin forever (see
    // `suspenseRetryDelayMs`). These override the root client's `retry: false` for THIS query only.
    // The budget is error-dependent: a deterministic failure keeps the short one, a transient
    // transport failure gets retried enough times to recover behind the loading fallback.
    retry: suspenseShouldRetry,
    retryDelay: suspenseRetryDelayMs,
  });

  if (result.data !== undefined && !result.isPlaceholderData) {
    // Retain the RAW cached value, not `result.data`: TanStack runs `select` over placeholder data
    // too, so retaining the already-selected value would apply `select` twice after a key rotation.
    lastGoodByPrefixRef.current.set(prefix, queryClient.getQueryData(options.queryKey));
  }

  // A query that has EXHAUSTED its retries with no data to show must surface the failure, not
  // suspend again. Re-suspending re-issues the same failing query through `ensureQueryData`, so the
  // boundary's fallback (a spinner) stays on screen forever while the loop reports every pass.
  // Throwing hands the error to the enclosing `SuspensePageBoundary`, which renders its
  // "Something went wrong" fallback with a working Retry button.
  if (result.data === undefined && result.isError) {
    const queryKeyHash = stringifyQueryKey(options.queryKey);
    // See the batch path: never hand `undefined` to the boundary.
    const error = result.error ?? new Error(`Suspense query failed: ${queryKeyHash}`);
    reportOnce(reportedFailedKeys, queryKeyHash, () => {
      consola.error("query failed; surfacing to the error boundary", {
        error,
        queryKey: options.queryKey,
      });
      captureSuspenseQueryError(error, options.queryKey);
    });

    throw error;
  }

  if (result.data === undefined) {
    // Time exactly how long this suspension keeps the boundary's fallback (the
    // loading spinner) on screen for this query key. The span nests under the
    // active navigation transaction, so a slow screen shows the suspending query
    // as the culprit on the Sentry trace waterfall. No-op unless tracing is
    // sampled. `instrumentSuspenseWait` returns the same promise we throw.
    throw instrumentSuspenseWait(
      stringPrefixKey(options.queryKey),
      { "han.suspense.query_key": truncateSentryTagValue(stringifyQueryKey(options.queryKey)) },
      queryClient.ensureQueryData(options),
    );
  }

  return result;
}) as typeof useSuspenseQueryFromTanstackReactQuery;

export const usePrefetchedSuspenseQueries: typeof useSuspenseQueriesFromTanstackReactQuery = (({
  queries,
}: {
  queries: readonly Parameters<typeof useQuery>[0][];
}) => {
  const queryClient = useQueryClient();
  const lastGoodByPrefixRef = useRef<Map<string, unknown>>(new Map());

  const prefixes = queries.map((options) => stringPrefixKey(options.queryKey));

  const results = useQueries({
    queries: queries.map((options, index) => ({
      ...options,
      placeholderData: () => {
        const prefix = prefixes[index];
        return prefix === undefined ? undefined : lastGoodByPrefixRef.current.get(prefix);
      },
      // See the single-query path: bounded, error-dependent backoff instead of the root client's
      // `retry: false`.
      retry: suspenseShouldRetry,
      retryDelay: suspenseRetryDelayMs,
    })),
  });

  for (const [index, result] of results.entries()) {
    if (result.data !== undefined && !result.isPlaceholderData) {
      const prefix = prefixes[index];
      const queryKey = queries[index]?.queryKey;
      if (prefix !== undefined && queryKey !== undefined) {
        // Raw cached value, not the selected `result.data` — see the single-query path.
        lastGoodByPrefixRef.current.set(prefix, queryClient.getQueryData(queryKey));
      }
    }
  }

  // Surface the FIRST exhausted failure rather than suspending on it forever (see the single-query
  // path). One broken query in the batch fails the whole batch: the others have nothing to render
  // into anyway, and a partial screen behind a permanent spinner is worse than the retryable error.
  const failed = results.find((result) => result.data === undefined && result.isError);
  if (failed !== undefined) {
    const failedQueryKey = queries[results.indexOf(failed)]?.queryKey ?? [];
    // `isError` guarantees an error, but the boundary must never receive `undefined` (React would
    // report an opaque "undefined was thrown"), so fall back to a keyed error.
    const error =
      failed.error ?? new Error(`Suspense query failed: ${stringifyQueryKey(failedQueryKey)}`);
    reportOnce(reportedFailedKeys, stringifyQueryKey(failedQueryKey), () => {
      consola.error("query failed; surfacing to the error boundary", {
        error,
        queryKey: failedQueryKey,
      });
      captureSuspenseQueryError(error, failedQueryKey);
    });

    throw error;
  }

  const isInitialPending = results.some((result) => result.data === undefined);

  if (isInitialPending) {
    const pendingQueryKeys = results.flatMap((result, index) => {
      if (result.data !== undefined) {
        return [];
      }

      const query = queries[index];
      return query === undefined ? [] : [query.queryKey];
    });

    // Time the combined suspension of the still-pending queries (see the single
    // -query path). Labelled by the pending query-key prefixes.
    throw instrumentSuspenseWait(
      pendingQueryKeys.map((queryKey) => stringPrefixKey(queryKey)).join(","),
      {
        "han.suspense.query_keys": truncateSentryTagValue(
          JSON.stringify(pendingQueryKeys.map((queryKey) => stringifyQueryKey(queryKey))),
        ),
      },
      Promise.all(queries.map((options) => queryClient.ensureQueryData(options))),
    );
  }

  return results;
}) as typeof useSuspenseQueriesFromTanstackReactQuery;
